/**
 * Date utility functions for handling timezone-related date conversions
 */

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * Convert a Date object (local date selection) to a date-only string.
 * This is used for API payloads where calendar dates should be timezone-agnostic.
 */
export const dateToDateOnlyStringLocal = (dateValue: Date | null | undefined): string | null => {
  if (!dateValue || isNaN(dateValue.getTime())) return null;

  const year = dateValue.getFullYear();
  const month = dateValue.getMonth() + 1;
  const day = dateValue.getDate();

  return `${year}-${pad2(month)}-${pad2(day)}`;
};

/**
 * Parse a date-only string (YYYY-MM-DD) into a local Date object at midnight.
 */
export const parseDateOnlyStringToLocalDate = (
  dateValue: string | null | undefined
): Date | null => {
  if (!dateValue) return null;

  const trimmed = dateValue.trim();
  const match = DATE_ONLY_REGEX.exec(trimmed);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (
    localDate.getFullYear() !== year ||
    localDate.getMonth() !== month - 1 ||
    localDate.getDate() !== day
  ) {
    return null;
  }

  return localDate;
};

/**
 * Normalize any calendar-date-like value to YYYY-MM-DD.
 * - Date values are treated as local calendar picks.
 * - Date-only strings are preserved.
 * - ISO timestamp strings are interpreted by UTC calendar date.
 */
export const normalizeCalendarDateToDateOnlyString = (
  value: Date | string | null | undefined
): string | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return dateToDateOnlyStringLocal(value);
  }

  const trimmed = value.trim();
  const dateOnly = parseDateOnlyStringToLocalDate(trimmed);
  if (dateOnly) {
    return dateToDateOnlyStringLocal(dateOnly);
  }

  const parsed = new Date(trimmed);
  if (isNaN(parsed.getTime())) return null;

  return `${parsed.getUTCFullYear()}-${pad2(parsed.getUTCMonth() + 1)}-${pad2(parsed.getUTCDate())}`;
};

/**
 * Convert a UTC date string to a Date object representing the same calendar date in local timezone.
 * This prevents date shifts when displaying UTC dates in date inputs.
 *
 * @param dateValue - Date string or Date object (expected to be at UTC midnight)
 * @returns Date object at local midnight with the same calendar date, or null if invalid
 *
 * @example
 * // UTC date: 2024-01-14T00:00:00.000Z (midnight UTC on Jan 14)
 * // Without conversion: displays as Jan 13 in San Francisco (UTC-8)
 * // With conversion: displays as Jan 14 in any timezone
 */
export const convertUTCToLocalDate = (dateValue: Date | string | null | undefined): Date | null => {
  if (!dateValue) return null;

  if (typeof dateValue === 'string') {
    const localDateFromDateOnly = parseDateOnlyStringToLocalDate(dateValue);
    if (localDateFromDateOnly) return localDateFromDateOnly;
  }

  const utcDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (isNaN(utcDate.getTime())) return null;

  // Extract UTC date components (which represent the calendar date we want to preserve)
  return new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
    0,
    0,
    0,
    0
  );
};
